<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'

import { BoxComponent } from '@/ui-library'

withDefaults(
  defineProps<{
    type?: string
    rows?: number
    placeholder?: string
    size?: 'default' | 'small' | 'large'
    maxlength?: string
    name?: string
    isDisabled?: boolean
  }>(),
  {
    type: 'text',
    rows: 1,
    placeholder: '',
    size: 'default',
    maxlength: '',
    name: undefined,
    isDisabled: false,
  },
)
const [value, modifiers] = defineModel<string | number | undefined, 'number' | 'integer'>('modelValue', { default: '' })
defineSlots<{ header(): void; suffix(): void; prefix(): void }>()
defineEmits(['change', 'input'])
const slots = useSlots()

const DECIMAL_TEXT_PATTERN = /^-?\d*[.,]?\d*$/
const INTEGER_TEXT_PATTERN = /^\d*$/

const isNumeric = !!modifiers.number || !!modifiers.integer
const inputMode = modifiers.integer ? 'numeric' : 'decimal'

const toText = (modelValue: typeof value.value) => String(modelValue ?? '')
const toModel = (text: string) => {
  if (!isNumeric) {
    return text
  }
  return /\d/.test(text) ? Number(text.replace(',', '.')) : undefined
}
const isAllowedText = (text: string) => {
  if (modifiers.integer) {
    return INTEGER_TEXT_PATTERN.test(text)
  }
  return !modifiers.number || DECIMAL_TEXT_PATTERN.test(text)
}

const text = ref(toText(value.value))
const inputText = computed({
  get: () => text.value,
  set: (newText: string) => {
    if (isAllowedText(newText)) {
      text.value = newText
    }
  },
})

watch(text, (newText) => {
  value.value = toModel(newText)
})
watch(value, (modelValue) => {
  if (!Object.is(toModel(text.value), modelValue)) {
    text.value = toText(modelValue)
  }
})
</script>

<template>
  <div>
    <div v-if="slots.header">
      <BoxComponent :mb="8">
        <slot name="header" />
      </BoxComponent>
    </div>
    <el-input
      v-model="inputText"
      :type="isNumeric ? 'text' : type"
      :inputmode="isNumeric ? inputMode : undefined"
      :rows="rows"
      :placeholder="placeholder"
      :size="size"
      :maxlength="maxlength"
      :name="name"
      :disabled="isDisabled"
      @change="(data: string) => $emit('change', data)"
      @input="(data: string) => $emit('input', data)"
    >
      <template v-if="slots.prefix" #prefix>
        <slot name="prefix" />
      </template>
      <template v-if="slots.suffix" #suffix>
        <slot name="suffix" />
      </template>
    </el-input>
  </div>
</template>

<style scoped lang="scss">
:deep(.el-input__wrapper) {
  height: 56px;
  padding: 16px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12);

  &:hover,
  &.is-focused {
    box-shadow: 0 0 0 1px var(--secondary-64);
  }
}

:deep(.el-input--small .el-input__wrapper) {
  height: 40px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 24px;
}

:deep(.el-textarea__inner) {
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12);

  &:hover,
  &.is-focused {
    box-shadow: 0 0 0 1px var(--secondary-64);
  }
}
</style>
